import { FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import {
  CardapioPedido,
  CARDAPIO_PEDIDO_STATUS,
  ClienteAutocomplete,
  clienteOptionLabel,
  clienteWristbandKey,
  fetchCardapioPedidos,
} from "../utils/cardapio.api";
import {
  CaixaEmployee,
  fetchEmployees,
} from "../../caixa-financeiro/utils/caixa.api";
import {
  formatCurrency,
  effectiveCommissionPercent,
  mergePedidoCartLines,
  pedidosByWristband,
  sumPedidosCommission,
  sumPedidosTotal,
  formatPedidosWaiterNames,
  printConferenceNote,
} from "../utils/cardapio.utils";
import { ClienteAutocompleteField } from "./ClienteAutocompleteField";
import { CardapioPedidoPaymentModal } from "./CardapioPedidoPaymentModal";

interface Props {
  show: boolean;
  onClose: () => void;
}

/**
 * Modal do ícone Pagar no topo:
 * autocomplete de cliente (clientes_v) → consumo em aberto → Imprimir Nota / Pagar.
 */
export const TopbarPagarModal: FC<Props> = ({ show, onClose }) => {
  const [selectedCliente, setSelectedCliente] =
    useState<ClienteAutocomplete | null>(null);
  const [pedidos, setPedidos] = useState<CardapioPedido[]>([]);
  const [employees, setEmployees] = useState<CaixaEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const wristband = selectedCliente
    ? clienteWristbandKey(selectedCliente)
    : "";

  const clientePedidos = useMemo(
    () => (wristband ? pedidosByWristband(pedidos, wristband) : []),
    [pedidos, wristband],
  );

  const lines = useMemo(
    () => mergePedidoCartLines(clientePedidos),
    [clientePedidos],
  );
  const total = useMemo(
    () => sumPedidosTotal(clientePedidos),
    [clientePedidos],
  );
  const waiterName = useMemo(
    () => formatPedidosWaiterNames(clientePedidos),
    [clientePedidos],
  );
  const commissionPercent = useMemo(
    () => effectiveCommissionPercent(clientePedidos[0]?.commission_percent),
    [clientePedidos],
  );
  const commissionValue = useMemo(
    () => sumPedidosCommission(clientePedidos),
    [clientePedidos],
  );

  const paymentEmployee = useMemo(() => {
    if (!waiterName) return employees[0] ?? null;
    const names = waiterName.split(",").map((n) => n.trim());
    return (
      employees.find((e) => names.includes(e.name)) ?? employees[0] ?? null
    );
  }, [employees, waiterName]);

  const canAct = lines.length > 0 && !!wristband;

  useEffect(() => {
    if (!show) {
      setSelectedCliente(null);
      setPedidos([]);
      setPaymentOpen(false);
      return;
    }
    fetchEmployees()
      .then(setEmployees)
      .catch(() => setEmployees([]));
  }, [show]);

  useEffect(() => {
    if (!show || !selectedCliente || !wristband) {
      setPedidos([]);
      return;
    }
    setLoading(true);
    fetchCardapioPedidos(wristband, CARDAPIO_PEDIDO_STATUS.EM_ABERTO)
      .then(setPedidos)
      .catch(() => setPedidos([]))
      .finally(() => setLoading(false));
  }, [show, selectedCliente, wristband]);

  const handlePrint = () => {
    if (!canAct) {
      Swal.fire(
        "Atenção",
        "Selecione um cliente com consumo em aberto para imprimir.",
        "warning",
      );
      return;
    }
    const ok = printConferenceNote({
      clientName: selectedCliente
        ? clienteOptionLabel(selectedCliente)
        : wristband || "—",
      issuedAt: new Date().toLocaleString("pt-BR"),
      lines,
      total,
      commissionPercent,
      commissionValue,
    });
    if (!ok) {
      Swal.fire(
        "Atenção",
        "Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.",
        "warning",
      );
    }
  };

  const handlePay = () => {
    if (!canAct) {
      Swal.fire(
        "Atenção",
        "Selecione um cliente com consumo em aberto para pagar.",
        "warning",
      );
      return;
    }
    if (!paymentEmployee) {
      Swal.fire(
        "Atenção",
        "Garçom do pedido não encontrado entre os funcionários cadastrados.",
        "warning",
      );
      return;
    }
    setPaymentOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentOpen(false);
    window.dispatchEvent(new Event("cardapio:pedido-changed"));
    window.dispatchEvent(new Event("caixa:session-changed"));
    Swal.fire("Sucesso", "Pagamento registrado com sucesso!", "success");
    onClose();
  };

  if (!show || typeof document === "undefined") return null;

  return createPortal(
    <>
      {!paymentOpen && (
        <>
          <div
            className="modal fade show d-block"
            role="dialog"
            tabIndex={-1}
            style={{ zIndex: 1055 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h3 className="modal-title fw-bold">Pagar consumo</h3>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={onClose}
                  />
                </div>
                <div className="modal-body">
                  <ClienteAutocompleteField
                    id="topbar-pagar-cliente"
                    value={selectedCliente}
                    onChange={setSelectedCliente}
                    className="mb-5"
                  />

                  {!selectedCliente && (
                    <p className="text-muted fs-7 mb-0">
                      Busque o cliente por nome, CPF ou pulseira para ver o
                      consumo em aberto.
                    </p>
                  )}

                  {selectedCliente && (
                    <>
                      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <div>
                          <div className="fw-semibold text-gray-800">
                            {clienteOptionLabel(selectedCliente)}
                          </div>
                          <div className="text-muted fs-7">
                            {loading
                              ? "Carregando consumo..."
                              : clientePedidos.length
                                ? `${clientePedidos.length} pedido(s) em aberto · Garçom: ${waiterName || "—"}`
                                : "Nenhum consumo em aberto para este cliente."}
                          </div>
                        </div>
                        <div className="fs-3 fw-bold text-primary">
                          {formatCurrency(total)}
                        </div>
                      </div>

                      <div className="table-responsive border rounded">
                        <table className="table align-middle gs-0 gy-3 mb-0">
                          <thead>
                            <tr className="fw-bold text-muted bg-light">
                              <th className="ps-4">Item</th>
                              <th className="text-end">Qtd</th>
                              <th className="text-end">Unit.</th>
                              <th className="text-end pe-4">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading && (
                              <tr>
                                <td colSpan={4} className="text-center py-5">
                                  Carregando...
                                </td>
                              </tr>
                            )}
                            {!loading && lines.length === 0 && (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="text-center py-5 text-muted"
                                >
                                  Sem itens em aberto
                                </td>
                              </tr>
                            )}
                            {!loading &&
                              lines.map((line) => (
                                <tr key={line._id_cardapio_item}>
                                  <td className="ps-4 text-gray-800">
                                    {line.item_name}
                                  </td>
                                  <td className="text-end">{line.quantity}</td>
                                  <td className="text-end">
                                    {formatCurrency(line.unit_price)}
                                  </td>
                                  <td className="text-end pe-4">
                                    {formatCurrency(
                                      line.unit_price * line.quantity,
                                    )}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {Number(commissionValue) > 0 && (
                        <div className="text-muted fs-7 mt-3">
                          Comissão ({commissionPercent}%):{" "}
                          {formatCurrency(commissionValue)}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={onClose}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-light-primary"
                    onClick={handlePrint}
                    disabled={!canAct}
                  >
                    Imprimir Nota
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handlePay}
                    disabled={!canAct || !paymentEmployee}
                  >
                    Pagar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />
        </>
      )}

      {paymentEmployee && (
        <CardapioPedidoPaymentModal
          show={paymentOpen}
          onHide={() => setPaymentOpen(false)}
          amount={total}
          waiterId={paymentEmployee._id}
          waiterName={paymentEmployee.name}
          wristband={wristband}
          commissionPercent={commissionPercent}
          cart={lines}
          pedidoIds={clientePedidos.map((p) => p._id)}
          _id_usuario={selectedCliente?.cliente_id}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </>,
    document.body,
  );
};
